import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Publicacao {
  id: number;
  tipo: string;
  id_referencia: number;
  titulo: string;
  legenda: string | null;
  imagem_url: string | null;
  video_url: string | null;
  destinos: string[];
  status: string;
  erro: string | null;
  created_at: string;
  publicado_em: string | null;
}

interface ConfigTipo {
  tipo: string;
  ativo: boolean;
  destinos_default: string[];
}

const DESTINOS = [
  { key: 'instagram_story', label: 'Story Instagram' },
  { key: 'facebook_story', label: 'Story Facebook' },
  { key: 'facebook_feed', label: 'Post Facebook' },
];

const ROTULO_TIPO: { [tipo: string]: string } = {
  banda_destaque: 'Banda em destaque',
  album: 'Novo álbum',
  evento: 'Novo evento',
  take: 'Take',
  empresa: 'Empresa nova',
  blog_post: 'Novo post no blog',
};

const ROTULO_STATUS: { [status: string]: string } = {
  pendente: 'Pendentes',
  publicado: 'Publicadas',
  rejeitado: 'Rejeitadas',
  erro: 'Com erro',
};

const SocialPublicacoes: React.FC = () => {
  const [publicacoes, setPublicacoes] = useState<Publicacao[]>([]);
  const [config, setConfig] = useState<ConfigTipo[]>([]);
  const [statusFiltro, setStatusFiltro] = useState<string>('pendente');
  const [destinosSelecionados, setDestinosSelecionados] = useState<{ [id: number]: string[] }>({});
  const [processando, setProcessando] = useState<number | null>(null);
  const [regenerando, setRegenerando] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPublicacoes(statusFiltro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFiltro]);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchPublicacoes = async (status: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/social/publicacoes?status=${status}`);
      setPublicacoes(response.data);
      const selecionados: { [id: number]: string[] } = {};
      response.data.forEach((p: Publicacao) => {
        selecionados[p.id] = p.destinos || [];
      });
      setDestinosSelecionados(selecionados);
    } catch (err) {
      console.error('Erro ao carregar publicações:', err);
      setError('Erro ao carregar publicações.');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/social/config');
      setConfig(response.data);
    } catch (err) {
      console.error('Erro ao carregar config:', err);
    }
  };

  const toggleDestino = (id: number, destino: string) => {
    setDestinosSelecionados(prev => {
      const atuais = prev[id] || [];
      return {
        ...prev,
        [id]: atuais.includes(destino) ? atuais.filter(d => d !== destino) : [...atuais, destino],
      };
    });
  };

  const aprovar = async (id: number) => {
    const destinos = destinosSelecionados[id] || [];
    if (!destinos.length) {
      alert('Selecione pelo menos um destino.');
      return;
    }
    if (!window.confirm('Publicar agora nos destinos selecionados?')) return;
    setProcessando(id);
    try {
      await api.put(`/admin/social/publicacoes/${id}/aprovar`, { destinos });
      alert('Publicado com sucesso!');
      fetchPublicacoes(statusFiltro);
    } catch (err) {
      const axiosErr = err as any;
      const detalhe = axiosErr.response?.data?.falhas?.join('\n') || axiosErr.response?.data?.error || 'Erro ao publicar.';
      alert(`Falha ao publicar:\n${detalhe}`);
      fetchPublicacoes(statusFiltro);
    } finally {
      setProcessando(null);
    }
  };

  const rejeitar = async (id: number) => {
    if (!window.confirm('Rejeitar esta publicação? Ela não irá para as redes.')) return;
    setProcessando(id);
    try {
      await api.put(`/admin/social/publicacoes/${id}/rejeitar`);
      fetchPublicacoes(statusFiltro);
    } catch (err) {
      console.error('Erro ao rejeitar:', err);
      alert('Erro ao rejeitar publicação.');
    } finally {
      setProcessando(null);
    }
  };

  const regenerarArte = async (id: number) => {
    if (!window.confirm('Gerar uma nova arte com IA para esta publicação? Isso pode levar alguns minutos.')) return;
    setRegenerando(id);
    try {
      await api.put(`/admin/social/publicacoes/${id}/regenerar-arte`);
      await fetchPublicacoes(statusFiltro);
    } catch (err) {
      const axiosErr = err as any;
      const detalhe = axiosErr.response?.data?.error || 'Erro ao gerar arte com IA.';
      alert(`Falha ao gerar arte:\n${detalhe}`);
    } finally {
      setRegenerando(null);
    }
  };

  const atualizarConfig = async (tipo: string, mudancas: Partial<ConfigTipo>) => {
    try {
      await api.put(`/admin/social/config/${tipo}`, mudancas);
      fetchConfig();
    } catch (err) {
      console.error('Erro ao atualizar config:', err);
      alert('Erro ao atualizar configuração.');
    }
  };

  const toggleDestinoDefault = (cfg: ConfigTipo, destino: string) => {
    const novos = cfg.destinos_default.includes(destino)
      ? cfg.destinos_default.filter(d => d !== destino)
      : [...cfg.destinos_default, destino];
    atualizarConfig(cfg.tipo, { destinos_default: novos });
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.titulo}>Publicações Sociais</h1>
      <p style={styles.subtitulo}>
        Stories e posts gerados automaticamente (Instagram @tribhusbr / página Facebook Tribhus).
        Nada é publicado sem aprovação aqui.
      </p>

      {/* Configuração por tipo */}
      <div style={styles.configBox}>
        <h3 style={{ marginTop: 0 }}>Configuração por tipo de conteúdo</h3>
        {config.map(cfg => (
          <div key={cfg.tipo} style={styles.configLinha}>
            <label style={{ minWidth: 140, fontWeight: 'bold' }}>
              <input
                type="checkbox"
                checked={cfg.ativo}
                onChange={() => atualizarConfig(cfg.tipo, { ativo: !cfg.ativo })}
              />{' '}
              {ROTULO_TIPO[cfg.tipo] || cfg.tipo}
            </label>
            <span style={{ color: '#666', fontSize: 13, marginRight: 8 }}>Destinos padrão:</span>
            {DESTINOS.map(d => (
              <label key={d.key} style={styles.destinoLabel}>
                <input
                  type="checkbox"
                  checked={cfg.destinos_default.includes(d.key)}
                  onChange={() => toggleDestinoDefault(cfg, d.key)}
                />{' '}
                {d.label}
              </label>
            ))}
          </div>
        ))}
      </div>

      {/* Filtro de status */}
      <div style={{ marginBottom: 16 }}>
        {Object.keys(ROTULO_STATUS).map(s => (
          <button
            key={s}
            onClick={() => setStatusFiltro(s)}
            style={{
              ...styles.abaStatus,
              ...(statusFiltro === s ? styles.abaStatusAtiva : {}),
            }}
          >
            {ROTULO_STATUS[s]}
          </button>
        ))}
      </div>

      {loading && <p>Carregando...</p>}
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {!loading && !publicacoes.length && <p style={{ color: '#666' }}>Nenhuma publicação {ROTULO_STATUS[statusFiltro].toLowerCase()}.</p>}

      <div style={styles.grid}>
        {publicacoes.map(pub => (
          <div key={pub.id} style={styles.card}>
            {pub.video_url ? (
              <video src={pub.video_url} poster={pub.imagem_url || undefined} controls style={styles.preview} />
            ) : pub.imagem_url ? (
              <a href={pub.imagem_url} target="_blank" rel="noreferrer">
                <img src={pub.imagem_url} alt={pub.titulo} style={styles.preview} />
              </a>
            ) : (
              <div style={{ ...styles.preview, ...styles.semImagem }}>sem imagem</div>
            )}
            <div style={styles.cardCorpo}>
              <span style={styles.badgeTipo}>{ROTULO_TIPO[pub.tipo] || pub.tipo}</span>
              <h4 style={{ margin: '8px 0 4px' }}>{pub.titulo}</h4>
              <small style={{ color: '#888' }}>{new Date(pub.created_at).toLocaleString('pt-BR')}</small>
              {pub.erro && <p style={{ color: '#dc3545', fontSize: 13 }}>{pub.erro}</p>}

              {(pub.status === 'pendente' || pub.status === 'erro') && (
                <>
                  <div style={{ margin: '10px 0' }}>
                    {DESTINOS.map(d => (
                      <label key={d.key} style={{ ...styles.destinoLabel, display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={(destinosSelecionados[pub.id] || []).includes(d.key)}
                          onChange={() => toggleDestino(pub.id, d.key)}
                        />{' '}
                        {d.label}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      style={styles.btnIa}
                      disabled={processando === pub.id || regenerando === pub.id}
                      onClick={() => regenerarArte(pub.id)}
                    >
                      {regenerando === pub.id ? 'Gerando IA...' : 'Gerar arte IA'}
                    </button>
                    <button
                      style={styles.btnAprovar}
                      disabled={processando === pub.id || regenerando === pub.id}
                      onClick={() => aprovar(pub.id)}
                    >
                      {processando === pub.id ? 'Publicando...' : 'Aprovar e publicar'}
                    </button>
                    <button
                      style={styles.btnRejeitar}
                      disabled={processando === pub.id || regenerando === pub.id}
                      onClick={() => rejeitar(pub.id)}
                    >
                      Rejeitar
                    </button>
                  </div>
                </>
              )}

              {pub.status === 'publicado' && pub.publicado_em && (
                <p style={{ color: '#28a745', fontSize: 13, marginBottom: 0 }}>
                  Publicado em {new Date(pub.publicado_em).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: 20, maxWidth: 1200, margin: '0 auto' },
  titulo: { marginBottom: 4 },
  subtitulo: { color: '#666', marginTop: 0 },
  configBox: {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  configLinha: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    padding: '6px 0',
    borderBottom: '1px solid #eee',
  },
  destinoLabel: { fontSize: 13, marginRight: 12, color: '#333' },
  abaStatus: {
    padding: '8px 16px',
    marginRight: 8,
    border: '1px solid #ccc',
    borderRadius: 20,
    background: '#fff',
    cursor: 'pointer',
  },
  abaStatusAtiva: { background: '#212529', color: '#fff', borderColor: '#212529' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  card: {
    border: '1px solid #dee2e6',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
  },
  preview: { width: '100%', aspectRatio: '9 / 16', objectFit: 'cover', display: 'block' },
  semImagem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f1f3f5',
    color: '#999',
  },
  cardCorpo: { padding: 12 },
  badgeTipo: {
    background: '#E63950',
    color: '#fff',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 'bold',
  },
  btnAprovar: {
    flex: 1,
    padding: '8px 12px',
    background: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  btnIa: {
    flex: '1 0 100%',
    padding: '8px 12px',
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  btnRejeitar: {
    padding: '8px 12px',
    background: '#fff',
    color: '#dc3545',
    border: '1px solid #dc3545',
    borderRadius: 6,
    cursor: 'pointer',
  },
};

export default SocialPublicacoes;
